import React, { Suspense, useEffect, useState, lazy } from "react";

// Libraries
import { Routes, Route, useLocation } from "react-router-dom";
import retina from "retinajs";
import { AnimatePresence } from "framer-motion";

// Context
import GlobalContext from "./Context/Context";

// Components
import ScrollToTopButton from "./Components/ScrollToTop";
import Analytics from "./Components/Analytics/Analytics";

// LB pages
const LegacyBridgePage = lazy(() => import("./Pages/Home/LegacyBridge"));
const NotFoundPage = lazy(() => import("./Pages/404"));
const Privacy = lazy(() => import("./Pages/Privacy"));
const PrivacyPolicyPage = lazy(() => import("./Pages/PrivacyPolicy"));
const TermsOfServicePage = lazy(() => import("./Pages/TermsOfService"));

// BDEP Pages
const BdepPage = lazy(() => import("./Pages/Bdep/BdepPage"));

// Firm Product Landing Pages (FEAT-001)
const FirmProductLanding = lazy(() => import("./Pages/FirmLanding/FirmProductLanding"));

function App() {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customModal, setCustomModal] = useState({
    el: null,
    isOpen: false,
  });
  const location = useLocation();

  // RetinaJS
  useEffect(() => {
    window.addEventListener("load", retina(document.querySelectorAll("img")));
  }, []);

  useEffect(() => {
    setTimeout(() => {
      import("./Functions/Utilities").then((module) => {
        module.SetHeaderMenuPos();
        module.setDocumentFullHeight();
      });
    }, 1000);
  }, [location]);

  useEffect(() => {
    if (isModalOpen === true) {
      document.querySelector("body").classList.add("overflow-hidden");
    } else {
      document.querySelector("body").classList.remove("overflow-hidden");
    }
  }, [isModalOpen]);

  // Get the current location and set the window to top.
  // Guard: skip the scroll when navigation carries an anchor target — the
  // homepage's mount effect runs first (child-before-parent effect flush) and
  // would be cancelled without this guard. setFooterHeight and modal reset run
  // unconditionally so layout state stays clean on every route change.
  useEffect(() => {
    const hasAnchorTarget =
      location.hash ||
      (location.state && location.state.section)
    if (!hasAnchorTarget) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
    setFooterHeight(0);
    setCustomModal({
      ...customModal,
      el: null,
      isOpen: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <GlobalContext.Provider
      value={{
        headerHeight,
        setHeaderHeight,
        footerHeight,
        setFooterHeight,
        isModalOpen,
        setIsModalOpen,
        customModal,
        setCustomModal,
      }}
    >
      <div className="App" style={{ "--header-height": `${headerHeight}px` }}>
        <Analytics />
        {
          <main style={{ marginTop: headerHeight, marginBottom: footerHeight }}>
            <ScrollToTopButton />
            <AnimatePresence mode="wait">
              <Suspense fallback={<></>}>
                <Routes>
                  {/* LB home */}
                  <Route path="/" element={<LegacyBridgePage style={{ "--base-color": "#1A577B" }} />} />

                  {/* BDEP Pages */}
                  <Route
                    path="/bdep/:slug"
                    element={
                      <BdepPage style={{ "--base-color": "#1A577B" }} />
                    }
                  />

                  {/* Firm Product Landing Pages (FEAT-001) — Amendment A slugs (2026-07-06) */}
                  <Route path="/firm" element={<FirmProductLanding variantKey="A" />} />
                  <Route path="/firm/client-promise" element={<FirmProductLanding variantKey="B" />} />
                  <Route path="/firm/practice-growth" element={<FirmProductLanding variantKey="C" />} />
                  <Route path="/firm/digital-asset-planning" element={<FirmProductLanding variantKey="D" />} />
                  <Route path="/firm/trusted-advisor" element={<FirmProductLanding variantKey="E" />} />

                  {/* Legal / privacy */}
                  <Route
                    path="/page/privacy-policy"
                    element={
                      <PrivacyPolicyPage style={{ "--base-color": "#1A577B" }} />
                    }
                  />
                  <Route
                    path="/page/terms-of-service"
                    element={
                      <TermsOfServicePage style={{ "--base-color": "#1A577B" }} />
                    }
                  />
                  <Route
                    path="/privacy"
                    element={<Privacy style={{ "--base-color": "#0038e3" }} />}
                  />

                  {/* Error page — explicit route kept: BdepPage and FirmProductLanding
                      both Navigate to this path; catch-all would also serve it but
                      keeping the explicit route preserves the intended URL. */}
                  <Route
                    path="/page/error-404"
                    element={
                      <NotFoundPage style={{ "--base-color": "#0038e3" }} />
                    }
                  />

                  {/* Catch-all 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </main>
        }
      </div>
    </GlobalContext.Provider>
  );
}

export default App;
